import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef, Input } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { DynamicFormComponent } from '@shared/dynamic-form/dynamic-form.component';

import { SortService } from '@services/sort.service';
import { CallsService } from '@services/api/calls.service';
import {
    PatientAFDTutorForm,
    IneCcaaItem,
    InePrvItem,
    IneMcpItem,
} from '@interfaces/patient/patient.interface';
import { SelectOption } from '@interfaces/input-select-option.interface';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-patient-edit-afd',
    templateUrl: './afd.component.html',
    styleUrls: ['./afd.component.scss']
})
export class AfdComponent extends DynamicFormComponent implements OnInit, OnDestroy, OnChanges {

    @Input() tutors: PatientAFDTutorForm[] = [];
    @Input() initialCommunityId: string | null = null;
    @Input() initialProvinceId: string | null = null;
    @Input() initialMunicipalityId: number | null = null;

    tutorsFormArray: FormArray<FormGroup>;

    communityControl = new FormControl<string | null>(null, Validators.required);
    provinceControl = new FormControl<string | null>({ value: null, disabled: true }, Validators.required);
    municipalityControl = new FormControl<number | null>({ value: null, disabled: true }, Validators.required);

    communityOptions: SelectOption[] = [];
    provinceOptions: SelectOption[] = [];
    municipalityOptions: SelectOption[] = [];

    relationshipOptions: SelectOption[] = [
        { label: 'Hijo/a', value: 1 },
        { label: 'Hermano/a', value: 2 },
        { label: 'Cónyuge', value: 3 },
    ];

    private cascadeSubs: Subscription[] = [];

    constructor(
        protected override sortService: SortService,
        protected override cdr: ChangeDetectorRef,
        private callsService: CallsService,
    ) {
        super(sortService, cdr);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.tutorsFormArray = new FormArray<FormGroup>([]);

        if (this.tutors && this.tutors.length) {
            this.tutors.forEach(t => this.addTutor(t));
        }

        this.loadCommunities();
        this.setupCascadingListeners();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.tutorsFormArray) return; // not yet initialized

        if (changes['tutors']) {
            this.tutorsFormArray.clear();
            const newTutors = changes['tutors'].currentValue || [];
            newTutors.forEach((t: PatientAFDTutorForm) => this.addTutor(t));
        }

        if (changes['initialCommunityId'] || changes['initialProvinceId'] || changes['initialMunicipalityId']) {
            // Reset cascading dropdowns and reload
            this.communityControl.setValue(null, { emitEvent: false });
            this.provinceControl.setValue(null, { emitEvent: false });
            this.provinceControl.disable({ emitEvent: false });
            this.provinceOptions = [];
            this.municipalityControl.setValue(null, { emitEvent: false });
            this.municipalityControl.disable({ emitEvent: false });
            this.municipalityOptions = [];

            if (this.communityOptions.length > 0 && this.initialCommunityId) {
                this.communityControl.setValue(this.initialCommunityId, { emitEvent: false });
                this.loadProvinces(this.initialCommunityId, true);
            } else if (this.communityOptions.length === 0) {
                this.loadCommunities();
            }
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.cascadeSubs.forEach(s => s.unsubscribe());
    }

    private loadCommunities(): void {
        this.callsService.getIneCcaaList().subscribe(res => {
            const list: IneCcaaItem[] = res?.payload || [];
            this.communityOptions = list.map(c => ({ label: c.Name, value: c.Id }));

            if (this.initialCommunityId) {
                this.communityControl.setValue(this.initialCommunityId, { emitEvent: false });
                this.loadProvinces(this.initialCommunityId, true);
            }
        });
    }

    private loadProvinces(ccaaId: string, isInit: boolean = false): void {
        this.callsService.getInePrvMcpLists(ccaaId, null).subscribe(res => {
            const prvList: InePrvItem[] = res?.payload?.prvList || [];
            this.provinceOptions = prvList.map(p => ({ label: p.Name, value: p.Id }));
            this.provinceControl.enable({ emitEvent: false });

            if (isInit && this.initialProvinceId) {
                this.provinceControl.setValue(this.initialProvinceId, { emitEvent: false });
                this.loadMunicipalities(ccaaId, this.initialProvinceId, true);
            }
        });
    }

    private loadMunicipalities(ccaaId: string, prvId: string, isInit: boolean = false): void {
        this.callsService.getInePrvMcpLists(ccaaId, prvId).subscribe(res => {
            const mcpList: IneMcpItem[] = res?.payload?.mcpList || [];
            this.municipalityOptions = mcpList.map(m => ({ label: m.Name, value: m.Id }));
            this.municipalityControl.enable({ emitEvent: false });

            if (isInit && this.initialMunicipalityId) {
                this.municipalityControl.setValue(this.initialMunicipalityId, { emitEvent: false });
            }
        });
    }

    private setupCascadingListeners(): void {
        this.cascadeSubs.push(
            this.communityControl.valueChanges.subscribe(ccaaId => {
                this.provinceControl.setValue(null, { emitEvent: false });
                this.provinceControl.disable({ emitEvent: false });
                this.provinceOptions = [];

                this.municipalityControl.setValue(null, { emitEvent: false });
                this.municipalityControl.disable({ emitEvent: false });
                this.municipalityOptions = [];

                if (ccaaId) {
                    this.loadProvinces(ccaaId);
                }
            }),
            this.provinceControl.valueChanges.subscribe(prvId => {
                this.municipalityControl.setValue(null, { emitEvent: false });
                this.municipalityControl.disable({ emitEvent: false });
                this.municipalityOptions = [];

                const ccaaId = this.communityControl.value;
                if (ccaaId && prvId) {
                    this.loadMunicipalities(ccaaId, prvId);
                }
            })
        );
    }

    getCommunityId(): string | null {
        return this.communityControl.value;
    }

    getProvinceId(): string | null {
        return this.provinceControl.value;
    }

    getMunicipalityId(): number | null {
        return this.municipalityControl.value;
    }

    getMunicipalityName(): string {
        const id = this.municipalityControl.value;
        const option = this.municipalityOptions.find(o => o.value === id);
        return option?.label || '';
    }

    addTutor(tutor?: PatientAFDTutorForm): void {
        const group = new FormGroup({
            id: new FormControl(tutor?.id ?? null),
            name: new FormControl(tutor?.name ?? ''),
            surnames: new FormControl(tutor?.surnames ?? ''),
            relationship: new FormControl(tutor?.relationship ?? 0),
            phone: new FormControl(tutor?.phone ?? ''),
        });
        this.tutorsFormArray.push(group);
    }

    removeTutor(index: number): void {
        this.tutorsFormArray.removeAt(index);
    }

    getTutorsValue(): PatientAFDTutorForm[] {
        return this.tutorsFormArray.value as PatientAFDTutorForm[];
    }
}
